import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import MediaQuery from 'react-responsive';

// Default image configuration
const defaultImageConfig = {
  deviceSizes: [640, 750, 828, 1080, 1200, 1920],
  imageSizes: [16, 32, 48, 64, 96, 128, 256, 384],
  loader: ({ src, width, quality }) => `${src}?w=${width}&q=${quality || 75}`
};

// Image Config Context
const ImageConfigContext = React.createContext(defaultImageConfig);

// Image Loader Function
const defaultLoader = ({ src, width, quality }) => {
  return `${src}?w=${width}&q=${quality || 75}`;
};

const ImageElement = forwardRef(
  (
    {
      src,
      srcSet,
      sizes,
      height,
      width,
      decoding = 'auto',
      className,
      style,
      fetchPriority = 'auto',
      placeholder,
      lazy = true,
      unoptimized = false,
      onLoad,
      onError,
      alt,
      ...rest
    },
    ref
  ) => {
    const imgRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isErrored, setIsErrored] = useState(false);

    useEffect(() => {
      const handleLoad = () => {
        setIsLoaded(true);
        if (onLoad) onLoad();
      };
      const handleError = () => {
        setIsErrored(true);
        if (onError) onError();
      };

      const imgElement = imgRef.current;
      if (imgElement) {
        imgElement.addEventListener('load', handleLoad);
        imgElement.addEventListener('error', handleError);
      }

      return () => {
        if (imgElement) {
          imgElement.removeEventListener('load', handleLoad);
          imgElement.removeEventListener('error', handleError);
        }
      };
    }, [onLoad, onError]);

    return (
      <img
        ref={(el) => {
          imgRef.current = el;
          if (ref) {
            if (typeof ref === 'function') ref(el);
            else ref.current = el;
          }
        }}
        src={src}
        alt={alt}
        srcSet={srcSet}
        sizes={sizes}
        width={width}
        height={height}
        decoding={decoding}
        style={style}
        className={className}
        data-loaded={isLoaded}
        data-errored={isErrored}
        {...(lazy ? { loading: 'lazy' } : {})}
        {...rest}
      />
    );
  }
);

const Image = forwardRef((props, ref) => {
  const configContext = useContext(ImageConfigContext);
  const config = configContext || defaultImageConfig;
  const { loader = config.loader || defaultLoader, ...restProps } = props;

  const imgProps = {
    src: loader({ src: props.src, width: props.width, quality: props.quality }),
    srcSet: undefined,
    sizes: props.sizes || '100vw'
  };

  const mediaQueryProps = {};

  const minWidth = Number(props.minWidth);
  const maxWidth = Number(props.maxWidth);

  if (!isNaN(minWidth)) {
    mediaQueryProps.minWidth = minWidth;
  }

  if (!isNaN(maxWidth)) {
    mediaQueryProps.maxWidth = maxWidth;
  }

  if (isNaN(minWidth) && isNaN(maxWidth)) {
    mediaQueryProps.minWidth = 300;
  }

  return (
    <MediaQuery {...mediaQueryProps}>
      <ImageElement
        {...imgProps}
        {...restProps}
        ref={ref}
        placeholder={'empty'}
      />
    </MediaQuery>
  );
});

export { Image };
